import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { SolicitarFaturaButton } from '@/components/carteira/fatura/SolicitarFaturaButton';
import { SolicitarFaturaModal } from '@/components/faturas/SolicitarFaturaModal';
import { useDownloadFatura } from '@/hooks/useDownloadFatura';
import { usePlantaVivoSnapshot, LinhaDetalhe } from '@/hooks/usePlantaVivoSnapshot';
import { useFaturaManual } from '@/hooks/useFaturaManual';
import { useCarteiraClaroAtual } from '@/hooks/useCarteiraClaroAtual';
import { LinhasFaturaTable, LinhaEditada } from './LinhasFaturaTable';
import { RenovacaoComissionamentoPanel } from './RenovacaoComissionamentoPanel';
import { ContaRenovacaoCpf } from './ContaRenovacaoCpf';
import type { ComposicaoProposta } from '@/constants/planosCNPJ';
import { useUserProfile } from '@/hooks/useUserProfile';
import { FaturaAnaliseDetalhada } from '@/components/carteira/fatura/FaturaAnaliseDetalhada';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ClaroEmpresasHeader } from '@/components/dashboard/ClaroEmpresasHeader';
import { ModalRegrasRenovacao } from '@/components/carteira/fatura/ModalRegrasRenovacao';
import { ModalFaturaVisualizacao } from '@/components/carteira/fatura/ModalFaturaVisualizacao';
import { PlantaVivoUploader } from './PlantaVivoUploader';
import { useParqueMovelByCnpj } from '@/hooks/useParqueVivoByCnpj';
import { formatarTelefone } from '@/utils/formatters';
import { PlantaVivoDisplay } from './PlantaVivoDisplay';
import { supabase } from '@/integrations/supabase/client';
import { invokeWithRealError } from '@/lib/invokeWithRealError';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  FileText, Download, RefreshCw, Clock, Check, AlertTriangle, 
  Loader2, Send, SearchX, RotateCcw, Save, Phone, Pencil, X, Users, Smartphone, Upload, Calendar, CalendarRange, MapPin
} from 'lucide-react';

type DebitoAutomaticoLido = { ativo?: unknown; banco?: unknown };
type ResumoFatura = { avisos?: unknown; debito_automatico?: DebitoAutomaticoLido };

// AVISOS DA CLARO — a fatura carimba o mesmo boilerplate em quase toda conta:
// RGC 765 (regra de inadimplencia, 6.017 vezes na base), FUST/FUNTTEL (4.293),
// roaming internacional (6.436) e DANFE (1.550). Ruido faz o consultor parar de
// ler a faixa inteira.
// O que vira conversa e' "sua oferta mudara' a partir de Fev/26" (536) e o
// reajuste anual pelo IPCA (206).
// E' lista de RUIDO, nao lista branca: aviso novo que a Claro inventar aparece.
// Melhor mostrar um boilerplate desconhecido do que esconder uma mudanca de
// oferta que ninguem previu.
const AVISO_BOILERPLATE = /RGC\s*765|FUST|FUNTTEL|roaming internacional|DANFE/i;

function avisosQueImportam(resumo?: ResumoFatura): string[] {
  const brutos = resumo?.avisos;
  if (!Array.isArray(brutos)) return [];
  return (brutos as unknown[])
    .map((a) => String(a ?? '').trim())
    .filter((a) => a.length > 0 && !AVISO_BOILERPLATE.test(a));
}

// DEBITO AUTOMATICO — TRES estados, e o terceiro e' o que faz o dado prestar.
// `null` = a fatura nao fala do assunto. Imprimir "NAO" nesse caso e' o bug que
// fez o grupo "fora do debito" saltar de 0,6% para 18,8% sem um cliente trocar
// de banco. Por isso o traco, que na regua da casa e' o nao-medido.
function rotuloDebitoAutomatico(resumo?: ResumoFatura): string {
  const da = resumo?.debito_automatico;
  if (!da || typeof da.ativo !== 'boolean') return '—';
  if (!da.ativo) return 'NÃO';
  const banco = String(da.banco ?? '').trim();
  return banco ? `SIM · ${banco}` : 'SIM';
}
import { toast } from 'sonner';
import cloneDeep from 'lodash/cloneDeep';
import { FaturaCpfGerenteCard } from './FaturaCpfGerenteCard';
import { LinhaSemFaturaCard } from './LinhaSemFaturaCard';
import { mensagemDaFuncao } from '@/lib/erroDaFuncao';
import { buscarUltimaFaturaValida, comoAFaturaChegou } from '@/lib/faturaValida';
import { etapaDoCiclo, avisoDoCiclo } from '@/lib/cicloDaFatura';
import { useContaFixa } from '@/hooks/useContaFixa';
import { ContaFixaQuadro } from '@/components/faturas/ContaFixaQuadro';

interface LeadFaturaTabProps {
  clienteId?: string | null;
  cnpj: string;
  linhaPlanoInicial?: string | null;
  tempoContratoVoz?: number | null;
  leadId?: string | null;
  // Novos campos para o modal de solicitar fatura
  consultorId?: string | null;
  consultorNome?: string | null;
  equipeId?: string | null;
  equipeNome?: string | null;
  razaoSocial?: string | null;
  uf?: string | null;
  qtdLinhasVoz?: number | null;
  // Flag para identificar leads de portabilidade
  isPortabilidade?: boolean;
  // Tipo do lead para identificar MANUAL
  tipoLead?: string | null;
  tipoPessoa?: 'PF' | 'PJ';
  cpf?: string | null;
  composicaoRenovacao?: ComposicaoProposta | null;
  faturaUploadIdRenovacao?: string | null;
  linhasFaturaRenovacao?: Array<{
    faturaUploadId?: string;
    contaNumero?: string;
    competenciaFatura?: string;
    numero: string;
    gbAtual?: number;
    planoAtual?: string;
    valorAtual?: number;
    sugestaoPlanoId?: string | null;
    sugestaoPlanoNome?: string | null;
    sugestaoValor?: number;
    sugestaoDelta?: number | null;
    sugestaoFaixa?: number | null;
    sugestaoAcrescimo?: number;
    sugestaoTicketAlvoFaixa1?: number | null;
    sugestaoMotivo?: string;
    dataFidelidade?: string | null;
  }>;
  onComposicaoRenovacaoChange?: (composicao: ComposicaoProposta) => void;
  renovacaoSomenteLeitura?: boolean;
  modoAmplo?: boolean;
}

type Solicitacao = {
  id: string;
  created_at: string;
  status: string;
  linha_plano: string | null;
  linha_solicitante: string | null;
  erro_mensagem: string | null;
  fatura_arquivo_url: string | null;
  status_label: string | null;
  origem: string | null;
  fatura_id: string | null;
};

// Helper para formatar linha telefônica
const formatarLinha = (linha: string | number | null | undefined): string => {
  if (linha == null) return '';
  const digits = String(linha).replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  return String(linha);
};

function DicaVariasContas() {
  return (
    <div className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
      <p className="font-semibold text-foreground">Cliente com mais de uma conta Claro no mesmo CNPJ?</p>
      <p>Anexe um PDF de cada conta (pode selecionar vários de uma vez). As linhas de todas as contas aparecem juntas no simulador e na proposta de renovação. Da mesma conta vale só o mês mais recente.</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-4">
        <li><strong>Linha repetida trava:</strong> se o mesmo número estiver em duas contas do mesmo mês, a fatura nova não é gravada. Confira os PDFs.</li>
        <li><strong>Conta do SOLAR pode vir em imagem:</strong> o CRM lê mesmo assim — números, plano e valor. GB e minutos não aparecem numa conta escaneada.</li>
        <li><strong>Se não der para ler, não entra:</strong> o CRM avisa na hora e o PDF não vira a fatura do cliente. Baixe a conta de novo no SOLAR e anexe.</li>
        <li><strong>Proposta já aberta:</strong> depois de anexar, atualize a proposta para ela pegar as linhas da conta nova.</li>
      </ul>
    </div>
  );
}

/**
 * A aba Fatura tem as duas contas (Douglas 14/10/2026): a móvel, que monta a
 * renovação, e a fixa (internet/TV/fixo), ao lado dela e nunca no lugar.
 */
export function LeadFaturaTab(props: LeadFaturaTabProps) {
  const cnpjLimpo = String(props.cnpj ?? '').replace(/\D/g, '');
  const ehPj = (props.tipoPessoa ?? 'PJ') !== 'PF';
  const { data: fixa } = useContaFixa({ clienteId: props.clienteId, cnpj: props.clienteId ? null : cnpjLimpo }, ehPj);
  const [aba, setAba] = useState<'movel' | 'fixa' | null>(null);
  if (!ehPj || !fixa) return <LeadFaturaMovelTab {...props} />;
  const atual = aba ?? 'movel';
  return (
    <div className="space-y-3">
      <div className="inline-flex rounded-lg bg-[#EDE8E0] p-[3px] dark:bg-[#161010]" role="tablist" aria-label="Conta do cliente">
        {(['movel', 'fixa'] as const).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={atual === k} onClick={() => setAba(k)}
            className={`h-[26px] rounded-md px-3 text-[11.5px] font-semibold focus-visible:ring-2 focus-visible:ring-ring ${atual === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-primary'}`}>
            {k === 'movel' ? 'Móvel' : 'Fixa'}
          </button>
        ))}
      </div>
      {atual === 'fixa'
        ? <ContaFixaQuadro conta={fixa} cnpj={cnpjLimpo} razaoSocial={props.razaoSocial} />
        : <LeadFaturaMovelTab {...props} />}
    </div>
  );
}

function LeadFaturaMovelTab({ 
  clienteId, 
  cnpj: cnpjDoLead, 
  linhaPlanoInicial, 
  tempoContratoVoz, 
  leadId,
  consultorId,
  consultorNome,
  equipeId,
  equipeNome,
  razaoSocial,
  uf,
  qtdLinhasVoz,
  isPortabilidade = false,
  tipoPessoa = 'PJ',
  cpf = null,
  tipoLead,
  composicaoRenovacao,
  faturaUploadIdRenovacao,
  linhasFaturaRenovacao = [],
  onComposicaoRenovacaoChange,
  renovacaoSomenteLeitura = false,
  modoAmplo = false,
}: LeadFaturaTabProps) {
  const cnpj = String(cnpjDoLead ?? '').replace(/\D/g, '');
  const queryClient = useQueryClient();
  const { downloadFatura } = useDownloadFatura();
  const { data: userProfile } = useUserProfile();
  const [ressolicitando, setRessolicitando] = useState<string | null>(null);
  const [linhaEditando, setLinhaEditando] = useState('');
  const [salvandoLinha, setSalvandoLinha] = useState(false);
  const [linhaInicializada, setLinhaInicializada] = useState(false);
  const [modalSolicitarOpen, setModalSolicitarOpen] = useState(false);
  
  // Ref para upload direto de PDF
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Estados para modo de edição da análise
  const [isEditingAnalise, setIsEditingAnalise] = useState(false);
  const [editedAnalise, setEditedAnalise] = useState<any>(null);
  const [editedLinhas, setEditedLinhas] = useState<Map<string, LinhaEditada>>(new Map());

  const cnpjLimpo = cnpj ? String(cnpj).replace(/\D/g, '') : '';

  // TM (tempo de contrato voz) da ÚLTIMA importação da carteira Claro (página Carteira) —
  // fonte de verdade que atualiza ao subir o relatório; não "congela" como o campo do lead.
  // Fallback para o valor do lead se a carteira ainda não tiver esse CNPJ.
  const { data: carteiraAtual } = useCarteiraClaroAtual(cnpjLimpo);
  const tmCarteira = carteiraAtual?.tempo_contrato_voz ?? tempoContratoVoz ?? null;

  // Lead MANUAL = tipo_lead === 'MANUAL' (operadora ainda não definida)
  const isManual = tipoLead === 'MANUAL';
  const isRenovacaoClaro = tipoLead === 'RENOVACAO_CLARO';

  const linhasRenovacaoDaProposta = (composicaoRenovacao?.linhas || []).filter((linha) => linha.tipo === 'RENOVACAO');
  const propostaTemRenovacao = linhasRenovacaoDaProposta.length > 0;
  const faturaMontaAsLinhasDaRenovacao = isRenovacaoClaro || isManual;

  const faturaSmartPolling = useSmartPolling({ weight: 'B', baseInterval: POLLING_INTERVALS.PESO_B });

  // Hook para Planta Vivo (leads de Portabilidade e MANUAL)
  const { 
    snapshot: plantaVivoSnapshot, 
    isLoading: isLoadingPlantaVivo, 
    save: savePlantaVivo, 
    isSaving: isSavingPlantaVivo 
  } = usePlantaVivoSnapshot((isPortabilidade || isManual) ? leadId : null);

  // 29/08 (Douglas): a base Vivo JÁ tem o parque do cliente (linhas, plano,
  // fatura média, M, GB) — sem planta importada, mostrar a base em vez de uma
  // tela vazia pedindo Excel. A planta importada continua detalhando/substituindo.
  const { data: parqueBaseVivo = [] } = useParqueMovelByCnpj(
    (isPortabilidade || isManual) && !plantaVivoSnapshot ? cnpjLimpo : null
  );

  // Handler para importar Planta Vivo
  const handlePlantaVivoImport = (dados: {
    total_linhas: number;
    valor_total: number;
    gb_total: number;
    linhas_detalhadas: LinhaDetalhe[];
    dados_brutos: Record<string, unknown>;
  }) => {
    if (!leadId) {
      toast.error('Lead ID não encontrado');
      return;
    }
    savePlantaVivo({
      lead_id: leadId,
      cnpj: cnpjLimpo,
      ...dados,
    });
  };
  
  // Query para verificar solicitação manual pendente (Correção 4 do plano)

  // Hook para upload direto de fatura (Anexar PDF)
  const [enviandoLote, setEnviandoLote] = useState(false);
  const { uploadAndProcessAsync, isLoading: processandoFatura } = useFaturaManual({
    // lead CPF: a conta anexada fica pelo CPF, que é o documento da conta
    cnpj: tipoPessoa === 'PF' ? (cpf || '') : cnpj,
    clienteId: clienteId,
    razaoSocial: razaoSocial || undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fatura-analise-completa', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['ultima-fatura-upload', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['solicitacoes-fatura-cnpj', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['fatura-resumo-lead'] });
      queryClient.invalidateQueries({ queryKey: ['renovacao-cpf-contas'] });
    },
  });

  // Handler para upload direto de PDF
  const isUploadingDireto = enviandoLote || processandoFatura;
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivos = Array.from(e.target.files || []);
    e.target.value = '';
    setEnviandoLote(true);
    try {
      for (const arquivo of arquivos) {
        await uploadAndProcessAsync(arquivo).catch(() => undefined);
      }
    } finally { setEnviandoLote(false); }
  };

  // 1) Buscar linha do MASTER (clientes.linha_conta_fatura) por CNPJ
  const { data: clienteMaster, isLoading: isLoadingMaster } = useQuery({
    queryKey: ['cliente-linha-master', cnpjLimpo],
    queryFn: async () => {
      if (!cnpjLimpo) return null;
      const { data } = await supabase
        .from('clientes')
        .select('id, linha_conta_fatura')
        .eq('cnpj', cnpjLimpo)
        .maybeSingle();
      return data;
    },
    enabled: !!cnpjLimpo,
  });

  // 2) Buscar linha usada na última solicitação bem-sucedida
  const { data: ultimaSolicitacaoSucesso } = useQuery({
    queryKey: ['ultima-solicitacao-sucesso', cnpjLimpo],
    queryFn: async () => {
      if (!cnpjLimpo) return null;
      const { data } = await supabase
        .from('solicitacao_fatura')
        .select('linha_plano, created_at')
        .eq('cnpj', cnpjLimpo)
        .eq('status', 'FATURA_RECEBIDA')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!cnpjLimpo,
  });

  // Determinar a "melhor linha" para preencher o input (prioridade: master > linhaPlanoInicial > última solicitação)
  const linhaMaster = clienteMaster?.linha_conta_fatura?.replace(/\D/g, '');
  const linhaInicial = linhaPlanoInicial?.replace(/\D/g, '');
  const linhaUltimaSolicitacao = ultimaSolicitacaoSucesso?.linha_plano?.replace(/\D/g, '');

  const melhorLinha = 
    (linhaMaster && linhaMaster.length === 11 ? linhaMaster : null) ||
    (linhaInicial && linhaInicial.length === 11 ? linhaInicial : null) ||
    (linhaUltimaSolicitacao && linhaUltimaSolicitacao.length === 11 ? linhaUltimaSolicitacao : null) ||
    '';

  // Inicializar o input com a melhor linha (apenas uma vez)
  useEffect(() => {
    if (!linhaInicializada && !isLoadingMaster) {
      setLinhaEditando(melhorLinha);
      setLinhaInicializada(true);
    }
  }, [melhorLinha, linhaInicializada, isLoadingMaster]);

  // Salvar linha no MASTER (clientes.linha_conta_fatura)
  const salvarLinha = async () => {
    const linhaLimpa = linhaEditando.replace(/\D/g, '');
    
    if (linhaLimpa && linhaLimpa.length !== 11) {
      toast.error('Linha deve ter 11 dígitos (DDD + número)');
      return;
    }
    
    setSalvandoLinha(true);
    try {
      // FONTE DA VERDADE: grava na carteira (leads_carteira). O gatilho do banco
      // (sync_linha_leads_to_cliente) sincroniza a linha p/ o master `clientes`.
      if (cnpjLimpo) {
        const { error } = await supabase
          .from('leads_carteira')
          .update({
            linha_conta_fatura: linhaLimpa || null,
            conta_operadora: linhaLimpa || null
          })
          .eq('cnpj', cnpjLimpo);
        if (error) throw error;
        // Best-effort no master direto (gestor consegue; consultor já foi coberto pelo gatilho).
        await supabase
          .from('clientes')
          .update({ linha_conta_fatura: linhaLimpa || null })
          .eq('cnpj', cnpjLimpo);
      }
      
      // Invalidar queries relevantes
      queryClient.invalidateQueries({ queryKey: ['cliente-linha-master', cnpjLimpo] });
      queryClient.invalidateQueries({ queryKey: ['gestao-leads'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-carteira'] });
      queryClient.invalidateQueries({ queryKey: ['oportunidades-renovacao'] });
      toast.success('Linha salva no cadastro do cliente!');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar linha');
    } finally {
      setSalvandoLinha(false);
    }
  };

  // Buscar análise completa da fatura mais recente (por CNPJ, não depende de cliente_id)
  const { data: analise, isLoading: isLoadingAnalise, refetch: refetchAnalise } = useQuery({
    queryKey: ['fatura-analise-completa', cnpj, faturaUploadIdRenovacao],
    queryFn: async () => {
      if (faturaUploadIdRenovacao) {
        const { data, error } = await supabase
          .from('fatura_analise')
          .select('id, json_resultado, created_at, fatura_id')
          .eq('fatura_id', faturaUploadIdRenovacao)
          .maybeSingle();
        if (error) throw error;
        return data;
      }

      // Primeiro buscar cliente_id pelo CNPJ
      const { data: cliente } = await supabase
        .from('clientes')
        .select('id')
        .eq('cnpj', cnpj)
        .maybeSingle();
      
      if (!cliente) return null;

      const { data, error } = await supabase
        .from('fatura_analise')
        .select(`
          id,
          json_resultado,
          created_at,
          fatura_upload!inner(id, cliente_id)
        `)
        .eq('fatura_upload.cliente_id', cliente.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      
      return {
        ...data,
        fatura_id: data.fatura_upload?.id
      };
    },
    enabled: !!(cnpj || faturaUploadIdRenovacao),
  });

  // ⛔ O anexo manual foi RECUSADO? (o leitor nem de imagem conseguiu provar).
  // Sem isto a tela nao sabe diferenciar "ninguem anexou" de "anexou e nao
  // entrou" — e foi essa diferenca que custou uma semana na A KASPER.
  const { data: anexoRecusado } = useQuery({
    queryKey: ['ultimo-anexo-recusado', cnpjLimpo],
    queryFn: async () => {
      if (!cnpjLimpo) return false;
      const { data } = await supabase
        .from('fatura_upload')
        .select('id, recusada_em')
        .eq('cnpj_fatura', cnpjLimpo)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return !!(data as { recusada_em?: string | null } | null)?.recusada_em;
    },
    enabled: !!cnpjLimpo,
    staleTime: 30_000,
  });

  // Buscar última fatura baixada (por CNPJ, não depende de cliente_id)
  const { data: ultimaFatura } = useQuery({
    queryKey: ['ultima-fatura-upload', cnpj],
    queryFn: async () => {
      // Primeiro buscar cliente_id pelo CNPJ
      const { data: cliente } = await supabase
        .from('clientes')
        .select('id')
        .eq('cnpj', cnpj)
        .maybeSingle();
      
      if (!cliente) return null;

      // A fatura do cliente e a ultima QUE O CODIGO LEU. Esta tela pegava o
      // ultimo created_at, lido ou nao: anexo vazio ficava na frente da fatura
      // boa e o consultor via "nao analisada" com a proposta sem linha.
      return await buscarUltimaFaturaValida(cliente.id);
    },
    enabled: !!cnpj,
  });

  // Mutation para processar/reprocessar fatura
  const processarMutation = useMutation({
    mutationFn: async (faturaId: string) => {
      const { data, error } = await supabase.functions.invoke('processar-fatura-claro', {
        body: { fatura_id: faturaId }
      });
      if (error) throw new Error(await mensagemDaFuncao(error, 'Falha ao processar fatura'));
      return data;
    },
    onSuccess: () => {
      toast.success('Fatura processada com sucesso!');
      refetchAnalise();
      queryClient.invalidateQueries({ queryKey: ['linhas-fatura', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['fatura-analise-completa', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['fatura-analise-linhas', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['ultima-fatura-upload', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['cliente-linha-master', cnpjLimpo] });
      queryClient.invalidateQueries({ queryKey: ['ultima-solicitacao-sucesso', cnpjLimpo] });
      queryClient.invalidateQueries({ queryKey: ['fatura-resumo-lead'] });
    },
    onError: (error: any) => {
      toast.error(`Erro ao processar: ${error.message}`);
    }
  });

  // Mutation para salvar edições da análise
  const salvarEdicaoMutation = useMutation({
    mutationFn: async (novoJsonResultado: any) => {
      if (!analise?.id) throw new Error('ID da análise não encontrado');
      
      const { error } = await supabase
        .from('fatura_analise')
        .update({ 
          json_resultado: novoJsonResultado,
          updated_at: new Date().toISOString()
        })
        .eq('id', analise.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Análise atualizada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['fatura-analise-completa', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['fatura-analise-linhas', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['linhas-fatura', cnpj] });
      setIsEditingAnalise(false);
      setEditedAnalise(null);
    },
    onError: (error: any) => {
      toast.error(`Erro ao salvar: ${error.message}`);
    }
  });

  // Iniciar modo de edição - inicializa editedLinhas com valores atuais
  const startEditing = useCallback(() => {
    if (analise?.json_resultado) {
      setEditedAnalise(cloneDeep(analise.json_resultado));
      
      // Inicializar editedLinhas com valores atuais das linhas
      const linhasAnalise = (analise.json_resultado as any)?.analise_detalhada?.linhas || [];
      const initialMap = new Map<string, LinhaEditada>();
      linhasAnalise.forEach((linha: any) => {
        const msisdnNorm = linha.msisdn?.replace(/\D/g, '') || '';
        if (msisdnNorm) {
          const gbNominal = linha.situacao_atual?.gb_nominal || 0;
          const gbBonus = linha.situacao_atual?.gb_bonus || 0;
          
          initialMap.set(msisdnNorm, {
            // Situação atual - fallback para 'valor' quando 'valor_mensal' não existe
            plano_atual: linha.situacao_atual?.plano || '',
            valor_atual: linha.situacao_atual?.valor_mensal || linha.situacao_atual?.valor || 0,
            gb_livre: gbNominal + gbBonus,
            uso_real: linha.situacao_atual?.uso_gb || 0,
            // Proposta
            gb_proposto: linha.proposta_otimizacao?.gb_livre || 0,
            gb_redes_proposto: linha.proposta_otimizacao?.gb_redes_sociais || 0,
            valor_proposto: linha.proposta_otimizacao?.valor_mensal || 0,
          });
        }
      });
      setEditedLinhas(initialMap);
      setIsEditingAnalise(true);
    }
  }, [analise]);

  // Cancelar edição
  const cancelEditing = useCallback(() => {
    setIsEditingAnalise(false);
    setEditedAnalise(null);
    setEditedLinhas(new Map());
  }, []);

  // Handler para atualizar linha específica
  const updateLinha = useCallback((msisdn: string, campo: keyof LinhaEditada, valor: string | number) => {
    setEditedLinhas(prev => {
      const updated = new Map(prev);
      const linha = updated.get(msisdn) || { 
        plano_atual: '', 
        valor_atual: 0, 
        gb_livre: 0, 
        uso_real: 0,
        gb_proposto: 0, 
        gb_redes_proposto: 0, 
        valor_proposto: 0 
      };
      updated.set(msisdn, { ...linha, [campo]: valor });
      return updated;
    });
  }, []);

  // Salvar edições com validação das regras AACE
  const saveEditing = useCallback(() => {
    if (!editedAnalise) return;
    
    // Sincronizar editedLinhas com editedAnalise
    const updatedAnalise = cloneDeep(editedAnalise);
    const linhasAnalise = updatedAnalise.analise_detalhada?.linhas || [];
    
    // Atualizar cada linha com valores editados (situação atual + proposta)
    linhasAnalise.forEach((linha: any) => {
      const msisdnNorm = linha.msisdn?.replace(/\D/g, '') || '';
      const edited = editedLinhas.get(msisdnNorm);
      if (edited) {
        // Atualizar situação atual - salvar ambos 'valor' e 'valor_mensal' para compatibilidade
        if (!linha.situacao_atual) linha.situacao_atual = {};
        linha.situacao_atual.plano = edited.plano_atual;
        linha.situacao_atual.valor = edited.valor_atual;
        linha.situacao_atual.valor_mensal = edited.valor_atual;
        linha.situacao_atual.gb_nominal = edited.gb_livre; // Simplificação: gb_livre = gb_nominal
        linha.situacao_atual.uso_gb = edited.uso_real;
        
        // Atualizar proposta
        if (!linha.proposta_otimizacao) linha.proposta_otimizacao = {};
        linha.proposta_otimizacao.gb_livre = edited.gb_proposto;
        linha.proposta_otimizacao.gb_redes_sociais = edited.gb_redes_proposto;
        linha.proposta_otimizacao.valor_mensal = edited.valor_proposto;
      }
    });
    
    // Recalcular resumo_executivo.situacao_atual com base nos valores editados
    const valorAtualTotal = linhasAnalise.reduce((sum: number, l: any) => 
      sum + (l.situacao_atual?.valor_mensal || l.situacao_atual?.valor || 0), 0);
    const gbAtualTotal = linhasAnalise.reduce((sum: number, l: any) => 
      sum + (l.situacao_atual?.gb_nominal || 0), 0);
    
    if (updatedAnalise.resumo_executivo?.situacao_atual) {
      updatedAnalise.resumo_executivo.situacao_atual.valor_total_mensal = valorAtualTotal;
      updatedAnalise.resumo_executivo.situacao_atual.gb_contratado = gbAtualTotal;
    }
    
    // Recalcular totais da proposta_final
    const gbTotal = linhasAnalise.reduce((sum: number, l: any) => 
      sum + (l.proposta_otimizacao?.gb_livre || 0) + (l.proposta_otimizacao?.gb_redes_sociais || 0), 0);
    const valorTotal = linhasAnalise.reduce((sum: number, l: any) => 
      sum + (l.proposta_otimizacao?.valor_mensal || 0), 0);
    
    if (updatedAnalise.proposta_final) {
      updatedAnalise.proposta_final.gb_total_contratado = gbTotal;
      updatedAnalise.proposta_final.valor_mensal_total = valorTotal;
      updatedAnalise.proposta_final.valor_por_linha = linhasAnalise.length > 0 ? valorTotal / linhasAnalise.length : 0;
    }
    
    // Recalcular economia
    const situacaoAtual = updatedAnalise.resumo_executivo?.situacao_atual;
    const propostaFinal = updatedAnalise.proposta_final;
    
    if (situacaoAtual && propostaFinal) {
      const economiaMensal = situacaoAtual.valor_total_mensal - propostaFinal.valor_mensal_total;
      propostaFinal.economia_mensal = Math.max(0, economiaMensal);
      propostaFinal.economia_anual = propostaFinal.economia_mensal * 12;
      propostaFinal.economia_percent = situacaoAtual.valor_total_mensal > 0 
        ? `${((economiaMensal / situacaoAtual.valor_total_mensal) * 100).toFixed(1)}%`
        : '0%';
    }
    
    salvarEdicaoMutation.mutate(updatedAnalise);
  }, [editedAnalise, editedLinhas, salvarEdicaoMutation]);

  // Handler para atualizar campos da análise
  const updateAnaliseField = useCallback((path: string, value: any) => {
    setEditedAnalise((prev: any) => {
      if (!prev) return prev;
      const updated = cloneDeep(prev);
      
      // Navegar pelo path e atualizar o valor
      const keys = path.split('.');
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        // Handle array notation like linhas[0]
        const arrayMatch = key.match(/(\w+)\[(\d+)\]/);
        if (arrayMatch) {
          obj = obj[arrayMatch[1]][parseInt(arrayMatch[2])];
        } else {
          obj = obj[key];
        }
      }
      
      const lastKey = keys[keys.length - 1];
      const arrayMatch = lastKey.match(/(\w+)\[(\d+)\]/);
      if (arrayMatch) {
        obj[arrayMatch[1]][parseInt(arrayMatch[2])] = value;
      } else {
        obj[lastKey] = value;
      }
      
      return updated;
    });
  }, []);

  // Buscar solicitações de fatura pelo CNPJ
  const { data: solicitacoes, isLoading } = useQuery({
    queryKey: ['solicitacoes-fatura-cnpj', cnpj],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_resumo_solicitacoes_fatura')
        .select('id, created_at, status, linha_plano, linha_solicitante, erro_mensagem, fatura_arquivo_url, status_label, origem, fatura_id')
        .eq('cnpj', cnpj)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data as Solicitacao[];
    },
    enabled: !!cnpj,
    staleTime: 2 * 60 * 1000,
    refetchInterval: faturaSmartPolling.refetchInterval,
    refetchOnWindowFocus: false,
  });

  // ⛔ "Recebida" oito vezes seguidas não diz nada. No CONDOMINIO NORMANDIE
  // (24/09/2026) quatro delas eram a MESMA conta de agosto: o robô pediu
  // setembro cedo demais, o Bee devolveu agosto, e o CRM tentou de novo no dia
  // seguinte, até setembro sair. Medido em 30 dias: 2.837 de 6.388 recebidas
  // (44%) eram uma conta que já estava no CRM. A tela mostra o fato — até que
  // dia vai a conta, e se ela já tinha chegado — sem recriar a régua de
  // competência, que é uma só e mora no banco.
  const idsFaturas = (solicitacoes || []).map((s) => s.fatura_id).filter((id): id is string => !!id);
  const { data: contasRecebidas } = useQuery({
    queryKey: ['solicitacoes-fatura-contas', cnpj, idsFaturas.join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fatura_upload')
        .select('id, periodo_fim, duplicada_de, recusada_em')
        .in('id', idsFaturas);
      if (error) throw error;
      return new Map((data || []).map((f) => [f.id, f as {
        id: string; periodo_fim: string | null; duplicada_de: string | null; recusada_em: string | null;
      }]));
    },
    enabled: idsFaturas.length > 0,
    staleTime: 60_000,
  });

  const handleResolicitar = async (solicitacao: Solicitacao) => {
    if (!solicitacao.linha_plano) {
      toast.error('Linha do plano não disponível para resolicitar');
      return;
    }

    setRessolicitando(solicitacao.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Helper extrai motivo REAL quando edge function retorna non-2xx
      // (em vez do genérico "Edge Function returned a non-2xx status code")
      const { data, error, mensagem } = await invokeWithRealError('solicitar-fatura', {
        body: {
          cliente_id: clienteId,
          cnpj: cnpj,
          linha_plano: solicitacao.linha_plano,
          linha_solicitante: solicitacao.linha_solicitante || user?.phone || '',
          solicitante_id: user?.id
        }
      });

      if (error) throw new Error(mensagem || 'Erro desconhecido');
      // edge function agora retorna 200 + ok:false quando n8n falha — checar
      if (data && (data as any).ok === false) throw new Error((data as any).mensagem || (data as any).error || 'Falha ao solicitar fatura');

      queryClient.invalidateQueries({ queryKey: ['solicitacoes-fatura-cnpj', cnpj] });
      queryClient.invalidateQueries({ queryKey: ['todas-faturas'] });
      toast.success('Nova solicitação enviada!');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao resolicitar fatura');
    } finally {
      setRessolicitando(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'AGUARDANDO_ENVIO':
      case 'ENVIADO_N8N':
      case 'PROCESSANDO':
      case 'PROCESSANDO_BEE360':
        return <Loader2 className="h-3.5 w-3.5 animate-spin text-warning" />;
      case 'FATURA_RECEBIDA':
        return <Check className="h-3.5 w-3.5 text-success" />;
      case 'NAO_ENCONTRADO':
        return <SearchX className="h-3.5 w-3.5 text-warning" />;
      case 'ERRO_ENVIO':
      case 'ERRO_N8N':
      case 'ERRO_BEE360':
      case 'TIMEOUT':
        return <AlertTriangle className="h-3.5 w-3.5 text-danger" />;
      default:
        return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'AGUARDANDO_ENVIO':
      case 'ENVIADO_N8N':
      case 'PROCESSANDO':
      case 'PROCESSANDO_BEE360':
        return 'bg-warning/10 text-warning border-warning';
      case 'FATURA_RECEBIDA':
        return 'bg-success/10 text-success border-success';
      case 'NAO_ENCONTRADO':
        return 'bg-warning/10 text-warning border-warning';
      case 'ERRO_ENVIO':
      case 'ERRO_N8N':
      case 'ERRO_BEE360':
      case 'TIMEOUT':
        return 'bg-danger/10 text-danger border-danger';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const isProcessando = (status: string) => 
    ['AGUARDANDO_ENVIO', 'ENVIADO_N8N', 'PROCESSANDO', 'PROCESSANDO_BEE360'].includes(status);

  // 58b — a string técnica crua fica no title; a linha diz o que houve e o que fazer
  const erroLegivel = (s2: Solicitacao) => {
    if (s2.status === 'NAO_ENCONTRADO') return 'Fatura não encontrada para esta linha — confira o número ou peça manual.';
    if (s2.status === 'TIMEOUT') return 'O robô não respondeu — fora do horário (6h–21h) ou em manutenção. Resolicite ou peça manual.';
    return 'Falha do sistema ao baixar — resolicite a fatura.';
  };

  // Verificar se há solicitação em andamento com timeout de 2 horas (igual ao useSolicitarFatura)
  const temSolicitacaoEmAndamento = solicitacoes?.some(s => {
    if (!isProcessando(s.status)) return false;
    
    // Verificar timeout com fallback seguro (2 horas = 120 minutos)
    if (s.created_at) {
      try {
        const agora = Date.now();
        const criado = new Date(s.created_at).getTime();
        
        // Se data inválida (NaN), libera
        if (isNaN(criado)) return false;
        
        const diferencaMinutos = (agora - criado) / (1000 * 60);
        
        // Libera se passou mais de 2 horas
        if (diferencaMinutos >= 120) return false;
      } catch {
        // Erro ao parsear data - libera (fallback seguro)
        return false;
      }
    } else {
      // Sem created_at - libera (fallback seguro)
      return false;
    }
    
    return true; // Ainda em andamento e dentro do timeout
  });

  // Verificar se linha no master difere da última usada
  const linhaMasterValida = linhaMaster && linhaMaster.length === 11;
  const linhaUsadaValida = linhaUltimaSolicitacao && linhaUltimaSolicitacao.length === 11;
  const linhasDiferentes = linhaMasterValida && linhaUsadaValida && linhaMaster !== linhaUltimaSolicitacao;

  // ⛔ NO CPF NÃO EXISTE FATURA, TM NEM FAIXA (Douglas 17/09 e 25/09/2026).
  // A renovação CPF é linha, plano do catálogo CPF e valor — o que a proposta
  // e o pedido guardam. Nada da régua do CNPJ aparece aqui.
  // ⛔ LEAD CPF (Douglas 25/09/2026): a conta vem da gerente ou do PDF anexado;
  // a tela é a Conta + a Simulação de renovação aprovadas. Sem TM, faixa ou comissão.
  if (tipoPessoa === 'PF') {
    return (
      <div className={modoAmplo ? 'space-y-3' : 'space-y-4'}>
        {leadId && <FaturaCpfGerenteCard leadId={leadId} />}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-9 text-[12.5px] font-semibold"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingDireto || !cpf}
          >
            {isUploadingDireto
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processando...</>
              : <><Upload className="mr-2 h-4 w-4" />Anexar PDF</>}
          </Button>
          <input ref={fileInputRef} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={handleFileChange} />
          <span className="text-[11px] text-muted-foreground">
            {cpf ? 'Conta da pessoa física, baixada no SOLAR ou recebida da gerente.' : 'Lead sem CPF: preencha o CPF para anexar a conta.'}
          </span>
        </div>
        {leadId && (
          <ContaRenovacaoCpf
            leadId={leadId}
            composicao={composicaoRenovacao || null}
            onComposicaoChange={onComposicaoRenovacaoChange}
          />
        )}
      </div>
    );
  }

  return (
    <div className={modoAmplo ? 'space-y-3' : 'space-y-4'}>
      {/* Card destacado: Fatura CPF (Gerente Claro) — feature 26/05.
          Aparece só se a estratégia FATURA_CPF_GERENTE está ativa em
          /email-marketing → WhatsApp → Internas (Bee) → Estratégias.
          Permite pedir fatura do CPF direto pra Gerente Claro a partir do lead. */}
      {leadId && <FaturaCpfGerenteCard leadId={leadId} />}

      {/* Card Planta Vivo para leads de Portabilidade e MANUAL */}
      {(isPortabilidade || isManual) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-card-foreground">
              <Smartphone className="h-3 w-3 text-muted-foreground" />
              Plano atual · Vivo Empresas
              <Badge variant="outline" className="ml-auto border-border bg-[#F2EEE8] text-[9px] font-bold uppercase text-muted-foreground dark:bg-white/[0.06]">
                {isManual ? 'Identificação' : 'Móvel'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingPlantaVivo ? (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : plantaVivoSnapshot ? (
              <PlantaVivoDisplay 
                snapshot={plantaVivoSnapshot}
                leadId={leadId || undefined}
                onReimport={() => {
                  // Abre file picker via ref no Uploader
                  const fileInput = document.createElement('input');
                  fileInput.type = 'file';
                  fileInput.accept = '.xlsx,.xls';
                  fileInput.onchange = (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      // Criar um evento fake para o uploader
                      const fakeEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
                      // Não podemos chamar o handleFileSelect diretamente, então usamos um workaround
                      // Removemos o snapshot atual para mostrar o uploader
                      // Na prática, vamos apenas deixar o botão reimportar no display
                    }
                  };
                  fileInput.click();
                }}
                isReimporting={isSavingPlantaVivo}
                modoAmplo={modoAmplo}
              />
            ) : (
              <div className="space-y-4">
                {parqueBaseVivo.length > 0 && (() => {
                  const totalFat = parqueBaseVivo.reduce((soma, l) => soma + Number(l.fat_medio_03_meses || 0), 0);
                  const totalGb = parqueBaseVivo.reduce((soma, l) => soma + Number(l.qtd_gb_contratado_dados || 0), 0);
                  return (
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Parque da base Vivo ({parqueBaseVivo.length} linhas)</p>
                        <p className="text-[10px] text-muted-foreground">a planta importada detalha e atualiza estes dados</p>
                      </div>
                      <div className="mb-3 grid grid-cols-3 gap-2">
                        <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                          <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Linhas</p>
                          <p className="font-display text-[20px] font-bold leading-tight tabular-nums text-foreground">{parqueBaseVivo.length}</p>
                        </div>
                        <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                          <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Fatura média</p>
                          <p className="truncate font-display text-[20px] font-bold leading-tight tabular-nums text-money-highlight">{totalFat > 0 ? totalFat.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</p>
                        </div>
                        <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                          <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">GB contratado</p>
                          <p className="font-display text-[20px] font-bold leading-tight tabular-nums text-foreground">{totalGb > 0 ? totalGb : '—'}</p>
                        </div>
                      </div>
                      <div className={modoAmplo ? 'max-h-[50vh] overflow-y-auto rounded-lg border border-border' : 'max-h-56 overflow-y-auto rounded-lg border border-border'}>
                        <div className="sticky top-0 z-10 grid h-7 grid-cols-[120px_minmax(140px,1fr)_76px_48px_64px_64px] items-center gap-2 bg-muted px-2.5 text-[8.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                          <span>Telefone</span><span>Plano</span><span className="text-right">Fatura</span><span className="text-center">M</span><span className="text-right">GB cont.</span><span className="text-right">GB uso</span>
                        </div>
                        {parqueBaseVivo.map((l, i) => {
                          const m = Number(l.m ?? 0);
                          return (
                            <div key={`${l.nr_telefone}-${i}`} className="grid h-[30px] grid-cols-[120px_minmax(140px,1fr)_76px_48px_64px_64px] items-center gap-2 overflow-hidden border-t px-2.5 text-[11px] tabular-nums">
                              <span className="truncate font-bold">{formatarTelefone(l.nr_telefone)}</span>
                              <span className="truncate text-muted-foreground" title={l.plano || ''}>{l.plano || '—'}</span>
                              <span className="truncate text-right font-semibold">{l.fat_medio_03_meses ? Number(l.fat_medio_03_meses).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</span>
                              <span
                                className={`text-center font-semibold ${m >= 24 ? 'text-[#075E54] dark:text-[#25D366]' : m > 0 ? 'text-primary dark:text-[#C87484]' : 'text-muted-foreground/40'}`}
                                title={m > 0 ? (m >= 24 ? `M${m} — sem multa (M ≥ 24)` : `M${m} — porta com multa`) : 'M não informado'}
                              >
                                {m > 0 ? `M${m}` : '—'}
                              </span>
                              <span className="text-right text-muted-foreground">{l.qtd_gb_contratado_dados ? `${l.qtd_gb_contratado_dados} GB` : '—'}</span>
                              <span className="text-right text-muted-foreground">{l.qtd_gb_traf_dados ? `${l.qtd_gb_traf_dados} GB` : '—'}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                <PlantaVivoUploader 
                  onImport={handlePlantaVivoImport}
                  isLoading={isSavingPlantaVivo}
                  cnpjLead={cnpjLimpo}
                  leadId={leadId || undefined}
                  razaoSocial={razaoSocial}
                  compact={modoAmplo && parqueBaseVivo.length > 0}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {linhasFaturaRenovacao.some(linha => linha.faturaUploadId) && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-semibold">Contas disponíveis para a proposta · {linhasFaturaRenovacao.length} linhas</p>
            <p className="mt-1 text-xs text-muted-foreground">Anexe os PDFs das outras contas neste lead. Cada conta usa sua fatura válida mais recente.</p>
            {[...new Set(linhasFaturaRenovacao.map(linha => linha.faturaUploadId))].filter(Boolean).map(id => {
              const linhasConta = linhasFaturaRenovacao.filter(linha => linha.faturaUploadId === id);
              const conta = linhasConta[0];
              return <p key={id} className="mt-2 text-xs">Conta {conta.contaNumero || 'não identificada'} · {conta.competenciaFatura} · {linhasConta.length} linhas</p>;
            })}
          </CardContent>
        </Card>
      )}

      {/* §7 — a nota longa em bordô vira uma linha discreta */}
      {isPortabilidade && (
        <p className="flex items-center gap-1.5 px-1 text-[11px] text-muted-foreground">
          <Smartphone className="h-3 w-3 shrink-0" />
          lead de Portabilidade Vivo — fatura via "Solicitar Manual" (Qualidade) ou "Anexar PDF"; a planta Mapa Parque detalha o plano atual
        </p>
      )}

      {/* Alerta informativo para leads MANUAL */}
      {isManual && (
        <Alert className="border-primary/40 bg-primary/5">
          <AlertTriangle className="h-4 w-4 text-primary" />
          <AlertTitle className="font-semibold text-sm text-primary dark:text-[#C87484]">Lead sem operadora definida</AlertTitle>
          <AlertDescription className="text-sm mt-1 text-primary dark:text-[#C87484]">
            Importe a <strong>Planta Vivo</strong> acima se o cliente for da Vivo, ou use <strong>Solicitar Fatura / Anexar PDF</strong> abaixo para clientes Claro.
          </AlertDescription>
        </Alert>
      )}

      {/* ===== SEÇÃO ESPECÍFICA PARA RENOVAÇÃO CLARO ===== */}
      {!isPortabilidade && (
        <>
          {/* 58a — FATURA VIGENTE: card único (o banner vinho duplicava o hero) */}
          {analise && (() => {
            const resumo = (analise.json_resultado as any)?.resumo_executivo;
            if (!resumo) return null;
            const fmtCnpjRe = (v?: string) => {
              const d = String(v || '').replace(/\D/g, '');
              return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : (v || '');
            };
            const fmtDataIso = (v?: string) => {
              const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
              return m ? `${m[3]}/${m[2]}/${m[1]}` : (v || 'N/D');
            };
            return (
              <div className="relative overflow-hidden rounded-xl border border-border bg-card pl-1">
                <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
                <div className="flex h-[34px] items-center justify-between gap-3 border-b border-border px-4">
                  <p className="min-w-0 truncate text-[11px] font-bold uppercase tracking-[0.08em] text-foreground" title={resumo.cliente?.razao_social || ''}>
                    Fatura vigente{resumo.cliente?.razao_social ? ` · ${resumo.cliente.razao_social}` : ''}
                  </p>
                  <p className="flex-shrink-0 text-[10.5px] tabular-nums text-muted-foreground">
                    {fmtCnpjRe(resumo.cliente?.cnpj)}{resumo.cliente?.conta_numero ? <> · conta <b className="font-semibold text-foreground">{resumo.cliente.conta_numero}</b></> : null}{resumo.cliente?.numero_cliente ? <> · cliente <b className="font-semibold text-foreground">{resumo.cliente.numero_cliente}</b></> : null}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 xl:grid-cols-6">
                  {[
                    ['Valor da conta', resumo.situacao_atual?.valor_total_mensal != null ? `R$ ${Number(resumo.situacao_atual.valor_total_mensal).toFixed(2).replace('.', ',')}` : '—'],
                    ['Linhas', resumo.situacao_atual?.linhas_ativas != null ? String(resumo.situacao_atual.linhas_ativas) : '—'],
                    ['TM (carteira)', tmCarteira ? `${tmCarteira} meses` : '—'],
                    ['Vencimento', resumo.periodo?.vencimento ? fmtDataIso(resumo.periodo.vencimento) : '—'],
                    ['Período', resumo.periodo?.inicio ? `${fmtDataIso(resumo.periodo.inicio)} a ${fmtDataIso(resumo.periodo.fim)}` : '—'],
                    ['Débito automático', rotuloDebitoAutomatico(resumo)],
                  ].map(([rotulo, valor]) => (
                    <div key={rotulo} className="rounded-lg border border-border bg-muted/40 px-3 py-2">
                      <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{rotulo}</p>
                      <p className="mt-0.5 truncate text-sm font-bold tabular-nums text-foreground" title={String(valor)}>{valor}</p>
                    </div>
                  ))}
                </div>
                {resumo.cliente?.endereco && (
                  <div className="flex items-start gap-2 border-t border-border px-4 py-2.5">
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Endereço da conta</p>
                      <p className="truncate text-sm text-foreground" title={resumo.cliente.endereco}>{resumo.cliente.endereco}</p>
                    </div>
                  </div>
                )}
                {avisosQueImportam(resumo).length > 0 && (
                  <div className="flex items-start gap-2 border-t border-border px-4 py-2.5">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <div className="min-w-0 space-y-0.5">
                      <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Avisos da Claro</p>
                      {avisosQueImportam(resumo).map((aviso, i) => (
                        <p key={i} className="text-sm text-foreground" title={aviso}>{aviso}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Tabela de linhas da última fatura processada */}
          {(!isRenovacaoClaro || renovacaoSomenteLeitura) && (
            <LinhasFaturaTable
              clienteId={clienteId}
              cnpj={cnpj}
              tempoContratoVoz={tempoContratoVoz}
              leadId={leadId}
              isEditing={isEditingAnalise}
              editedLinhas={editedLinhas}
              onUpdateLinha={updateLinha}
            />
          )}

          {isRenovacaoClaro && renovacaoSomenteLeitura && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Pedido existente preservado</AlertTitle>
              <AlertDescription>Esta renovação permanece no formato legado e não recebe Delta retroativo. Para usar o comparativo novo, abra uma revisão da proposta e confirme novamente todas as linhas pela fatura.</AlertDescription>
            </Alert>
          )}

          {/* ⛔ QUEM ABRE ESTE PAINEL É O PRODUTO, NUNCA O TIPO DO LEAD
              (Douglas 06/09/2026): "a tabela Delta tem que ser procurada SE
              dentro da proposta tem o produto renovação; se não tem, não tem
              porque. Isso não tem nada a ver com o tipo do lead."
              Ele é o ÚNICO lugar da tela que grava `renovacao.mesCompetencia`
              e `renovacao.faturaUploadId` — e por só aparecer em
              RENOVACAO_CLARO/MANUAL, consultor que punha linha de renovação
              numa proposta de portabilidade, TAB ou TT não tinha onde apontar
              a fatura: 183 propostas ficaram sem competência e o gatilho da
              renovação estourava depois, no salvar.
              O tipo do lead segue mandando em UMA coisa só, que não é trava e
              sim origem: em RENOVACAO_CLARO/MANUAL o lead É a renovação do
              parque, então a fatura inteira monta a lista. Nos demais a fatura
              só preenche plano/valor/GB dos números que a proposta escolheu —
              sem isso, abrir o lead reescrevia sozinho uma proposta salva,
              enfiando nela todas as linhas da conta Claro. */}
          {(propostaTemRenovacao || faturaMontaAsLinhasDaRenovacao) && !renovacaoSomenteLeitura && onComposicaoRenovacaoChange && (
            <RenovacaoComissionamentoPanel
              cnpj={cnpj}
              composicao={composicaoRenovacao || null}
              linhasFatura={linhasFaturaRenovacao}
              reconciliarComAFaturaInteira={faturaMontaAsLinhasDaRenovacao}
              faturaUploadId={faturaUploadIdRenovacao}
              tempoContratoMeses={tmCarteira}
              classificacao={carteiraAtual?.classificacao ?? null}
              tmOrigemCarteira={carteiraAtual?.tempo_contrato_voz != null}
              userRole={userProfile?.role}
              onComposicaoChange={onComposicaoRenovacaoChange}
              tipoPessoa={tipoPessoa}
            />
          )}

          {analise && isRenovacaoClaro && (
            <div className="flex items-center flex-nowrap justify-between gap-2 overflow-hidden">
              <div className="min-w-0 overflow-hidden"><ModalRegrasRenovacao /></div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => processarMutation.mutate(analise.fatura_id)}
                disabled={processarMutation.isPending}
                className="shrink-0 whitespace-nowrap text-[11px] px-1.5"
              >
                {processarMutation.isPending ? <Loader2 className="mr-0.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-0.5 h-3.5 w-3.5" />}
                Reprocessar fatura
              </Button>
            </div>
          )}

          {/* Análise completa da fatura (igual ao ClienteDetailModal) */}
          {isLoadingAnalise && (
            <div className="space-y-3">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-60 w-full" />
            </div>
          )}
          
          {/* Botão Processar quando há fatura baixada mas SEM análise */}
          {ultimaFatura && !analise && (
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {comoAFaturaChegou((ultimaFatura as { origem?: string }).origem)} em{' '}
                        {format(new Date(ultimaFatura.created_at), "dd/MM/yyyy HH:mm")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Fatura ainda não foi analisada. Clique para processar a análise.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => processarMutation.mutate(ultimaFatura.id)}
                    disabled={processarMutation.isPending}
                    className="!bg-primary !text-white hover:!bg-primary/90"
                  >
                    {processarMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Processar Fatura
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Botão Reprocessar + Editar + Análise */}
          {analise && !isRenovacaoClaro && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                {/* Botão Regras AACE */}
                <div className="flex items-center gap-2">
                  <ModalRegrasRenovacao />
                </div>
                
                <div className="flex items-center gap-2">
                  {isEditingAnalise ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelEditing}
                        className="gap-1.5"
                      >
                        <X className="h-3.5 w-3.5" />
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveEditing}
                        disabled={salvarEdicaoMutation.isPending}
                        className="gap-1.5 bg-success hover:bg-success text-white"
                      >
                        {salvarEdicaoMutation.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Save className="h-3.5 w-3.5" />
                        )}
                        Salvar Alterações
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={startEditing}
                        className="gap-1.5"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar Análise
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => processarMutation.mutate(analise.fatura_id)}
                        disabled={processarMutation.isPending}
                        className="gap-1.5"
                      >
                        {processarMutation.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5" />
                        )}
                        Reprocessar Fatura
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {isEditingAnalise && (
                <Alert className="border-warning/40 bg-warning/5 dark:bg-warning/20">
                  <Pencil className="h-4 w-4 text-warning" />
                  <AlertDescription className="text-warning dark:text-warning text-sm">
                    <strong>Modo de edição ativo.</strong> Clique nos campos para editar os valores. As alterações serão salvas ao clicar em "Salvar Alterações".
                  </AlertDescription>
                </Alert>
              )}
              
              <ErrorBoundary
                fallback={(error) => (
                  <Alert variant="destructive" className="border-destructive/30">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Não foi possível exibir a análise</AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p className="text-sm">
                        A fatura foi processada mas alguns dados vieram incompletos do OCR. Você pode reprocessar ou reimportar a fatura para tentar novamente.
                      </p>
                      <p className="text-xs font-mono opacity-70 break-all">
                        {error.message}
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
              >
                <FaturaAnaliseDetalhada analise={analise} ocultarHeader={true} />
              </ErrorBoundary>
            </div>
          )}

          {/* Card com dados da conta na operadora - reorganizado */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                Dados da conta na operadora
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Linha usada na última solicitação bem-sucedida (read-only) */}
              {linhaUsadaValida && (
                <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-3">
                  <Phone className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Linha usada para baixar a última fatura
                    </p>
                    <p className="font-mono text-lg font-semibold text-foreground">
                      {formatarLinha(linhaUltimaSolicitacao)}
                    </p>
                    {ultimaSolicitacaoSucesso?.created_at && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        em {format(new Date(ultimaSolicitacaoSucesso.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                    )}
                  </div>
                  {/* Botão para usar esta linha se o master estiver diferente */}
                  {linhasDiferentes && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => setLinhaEditando(linhaUltimaSolicitacao || '')}
                    >
                      Usar no cadastro
                    </Button>
                  )}
                </div>
              )}

              {/* Aviso se linha do master difere da última usada */}
              {linhasDiferentes && (
                <Alert className="border-primary/40 bg-primary/5">
                  <AlertTriangle className="h-4 w-4 text-primary" />
                  <AlertDescription className="text-sm text-primary dark:text-[#C87484]">
                    A linha cadastrada ({formatarLinha(linhaMaster)}) difere da última usada para baixar fatura ({formatarLinha(linhaUltimaSolicitacao)}).
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Linha Principal (editável) - agora busca do master */}
              <div className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Linha principal · para solicitar fatura</p>
                <div className="flex items-center gap-2">
                  <Input
                    value={linhaEditando}
                    onChange={(e) => setLinhaEditando(e.target.value)}
                    placeholder="51999887766"
                    className="h-10 flex-1 tabular-nums"
                    maxLength={15}
                  />
                  <Button
                    size="sm"
                    onClick={salvarLinha}
                    disabled={salvandoLinha || linhaEditando.replace(/\D/g, '') === (linhaMaster || '')}
                    className="h-10 gap-1.5 border-0 bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-4 text-[12.5px] font-bold text-white shadow-[0_2px_6px_rgba(91,10,13,0.30)] transition-all hover:brightness-110 disabled:opacity-45 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
                  >
                    {salvandoLinha ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Salvar
                  </Button>
                </div>
                <p className="text-[10.5px] text-muted-foreground">
                  11 dígitos com DDD{linhaMasterValida ? ' · salvo no cadastro do cliente (master)' : ''}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 58b — SOLICITAR NOVA FATURA acima do histórico */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">
                  <Send className="h-3.5 w-3.5 text-muted-foreground" />
                  Solicitar nova fatura
                </CardTitle>
                <span className="text-[11px] text-muted-foreground">processada automaticamente ao chegar</span>
              </div>
            </CardHeader>
            <CardContent>
              {/* ⛔ A ORDEM: Bee primeiro, manual quando ele falha (Douglas
                  23/09/2026). Sem este aviso o consultor anexa antes da hora ou
                  fica sem saber que o robô desistiu — na A KASPER ele anexou
                  dois PDFs, nenhum foi lido, e a tela dizia "Fatura baixada". */}
              {(() => {
                const aviso = avisoDoCiclo(etapaDoCiclo({
                  temContaLida: !!ultimaFatura,
                  ultimoStatusBee: solicitacoes?.find((s) => s.origem !== 'UPLOAD_DIRETO')?.status,
                  ultimoAnexoRecusado: anexoRecusado,
                }));
                if (!aviso) return null;
                return (
                  <div
                    className={`mb-3 rounded-lg border-l-[3px] p-3 ${
                      aviso.ehFalha
                        ? 'border-l-danger bg-danger/5'
                        : 'border-l-primary bg-primary/5'
                    }`}
                  >
                    <p className={`text-[12.5px] font-semibold ${aviso.ehFalha ? 'text-danger' : 'text-primary'}`}>
                      {aviso.titulo}
                    </p>
                    <ul className="mt-1.5 space-y-1">
                      {aviso.passos.map((passo) => (
                        <li key={passo} className="text-[11.5px] text-muted-foreground leading-snug">
                          · {passo}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
              <div className="flex gap-2 flex-wrap">
                <SolicitarFaturaButton 
                  clienteId={clienteId || undefined} 
                  cnpj={cnpj}
                  linhaPlanoInicial={melhorLinha || linhaPlanoInicial || undefined}
                />
                <Button
                  variant="outline"
                  className="h-9 text-[12.5px] font-semibold"
                  onClick={() => setModalSolicitarOpen(true)}
                >
                  <Users className="mr-2 h-3.5 w-3.5" />
                  Solicitar manual
                </Button>
                <Button
                  variant="outline"
                  className="h-9 text-[12.5px] font-semibold"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingDireto}
                >
                  {isUploadingDireto ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Anexar PDF
                    </>
                  )}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
              {/* O botão não dizia o que acontece ao clicar. Douglas 23/09/2026:
                  "o botão do PDF está sem avisar o que fazer". */}
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                <strong className="text-foreground">Solicitar Fatura</strong> pede a conta ao robô — é sempre o primeiro caminho.
                {' '}<strong className="text-foreground">Anexar PDF</strong> é para quando o robô não achou: baixe a conta no SOLAR e
                solte o arquivo aqui. O CRM lê sozinho, inclusive conta escaneada, e só grava se conferir com o total da fatura.
              </p>
              <DicaVariasContas />
              {temSolicitacaoEmAndamento && !ultimaFatura && !analise && (
                <p className="text-xs text-primary mt-3 flex items-center gap-1.5 dark:text-[#C87484]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Solicitação automática em andamento... aguarde processamento
                </p>
              )}
            </CardContent>
          </Card>

          {/* Lista de solicitações anteriores */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                Solicitações anteriores
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                </div>
              ) : !solicitacoes?.length ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Nenhuma solicitação de fatura encontrada para este CNPJ.
                </p>
              ) : (
                <div className="space-y-2">
                  {solicitacoes.map((s) => (
                    <div 
                      key={s.id} 
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                    >
                      {/* Info */}
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {getStatusIcon(s.status)}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={getStatusBadgeClass(s.status)}>
                              {s.status_label || s.status}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(s.created_at), "dd/MM HH:mm", { locale: ptBR })}
                            </span>
                            {s.linha_plano && (
                              <span className="text-xs font-mono text-muted-foreground">
                                {formatarLinha(s.linha_plano)}
                              </span>
                            )}
                            {(() => {
                              const conta = s.fatura_id ? contasRecebidas?.get(s.fatura_id) : undefined;
                              if (!conta?.periodo_fim) return null;
                              return (
                                <span className="text-xs text-foreground">
                                  · conta até {format(new Date(`${conta.periodo_fim.slice(0, 10)}T12:00:00`), 'dd/MM/yyyy')}
                                </span>
                              );
                            })()}
                          </div>
                          {(() => {
                            const conta = s.fatura_id ? contasRecebidas?.get(s.fatura_id) : undefined;
                            if (conta?.recusada_em) {
                              return (
                                <p className="text-xs mt-1 text-danger">
                                  Não foi lida — esta conta não entrou no CRM.
                                </p>
                              );
                            }
                            if (conta?.duplicada_de) {
                              return (
                                <p className="text-xs mt-1 text-muted-foreground">
                                  Repetida: é a mesma conta, do mesmo mês, que já tinha chegado antes.
                                </p>
                              );
                            }
                            return null;
                          })()}
                          {s.erro_mensagem && (
                            <p className="text-xs mt-1 text-primary dark:text-[#C87484]" title={s.erro_mensagem}>
                              {erroLegivel(s)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Ações */}
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        {/* Download quando disponível */}
                        {s.fatura_arquivo_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 text-success border-success hover:bg-success/5 hover:text-success"
                            onClick={() => downloadFatura(s.fatura_arquivo_url!)}
                          >
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            Download
                          </Button>
                        )}

                        {/* Resolicitar quando erro ou timeout */}
                        {['ERRO_ENVIO', 'ERRO_N8N', 'ERRO_BEE360', 'NAO_ENCONTRADO', 'TIMEOUT'].includes(s.status) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 text-primary border-primary/40 hover:bg-primary/5 hover:text-primary"
                            onClick={() => handleResolicitar(s)}
                            disabled={ressolicitando === s.id}
                          >
                            {ressolicitando === s.id ? (
                              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                            )}
                            Resolicitar
                          </Button>
                        )}

                        {/* Indicador de processando — mostra há quanto tempo está esperando o Bee.
                            Antes era só "Aguardando..." mudo. Agora consultor vê o tempo real. */}
                        {isProcessando(s.status) && (() => {
                          const min = Math.max(0, Math.floor((Date.now() - new Date(s.created_at).getTime()) / 60000));
                          const label = min < 60
                            ? `Aguardando Bee há ${min}min`
                            : `Aguardando Bee há ${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}`;
                          return (
                            <span className="text-xs text-muted-foreground" title="Robô do Bee processa em horário comercial (6h-21h dias úteis). Fora dessa janela pode demorar mais.">
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Card Solicitar para PORTABILIDADE (nos demais tipos ele aparece ACIMA do histórico) */}
      {isPortabilidade && (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-4 w-4" />
              Solicitar Nova Fatura
            </CardTitle>
            <span className="text-[11px] text-muted-foreground">processada automaticamente ao chegar</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {/* Botão Bee360 - APENAS para Renovação Claro */}
            {!isPortabilidade && (
              <SolicitarFaturaButton 
                clienteId={clienteId || undefined} 
                cnpj={cnpj}
                linhaPlanoInicial={melhorLinha || linhaPlanoInicial || undefined}
              />
            )}
            
            {/* Botão Manual - disponível para TODOS os tipos de lead */}
            <Button
              variant="outline"
              onClick={() => setModalSolicitarOpen(true)}
            >
              <Users className="h-4 w-4 mr-2" />
              Solicitar Manual
            </Button>
            
            {/* Botão Anexar PDF - Upload direto - disponível para TODOS os tipos de lead */}
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingDireto}
            >
              {isUploadingDireto ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Anexar PDF
                </>
              )}
            </Button>
            
            {/* Input hidden para upload de arquivo */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="application/pdf,image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
          <DicaVariasContas />
          {!isPortabilidade && temSolicitacaoEmAndamento && !ultimaFatura && !analise && (
            <p className="text-xs text-primary mt-3 flex items-center gap-1.5 dark:text-[#C87484]">
              <Loader2 className="h-3 w-3 animate-spin" />
              Solicitação automática em andamento... aguarde processamento
            </p>
          )}
        </CardContent>
      </Card>
      )}

      {userProfile?.role === 'master' && !isPortabilidade && cnpjLimpo.length === 14 && (
        <LinhaSemFaturaCard cnpj={cnpjLimpo} />
      )}

      {/* Modal de confirmação pra solicitar fatura via Bee360 */}
      {leadId && (
        <SolicitarFaturaModal
          open={modalSolicitarOpen}
          onOpenChange={setModalSolicitarOpen}
          razaoSocial={razaoSocial || ''}
          linhaContaFatura={melhorLinha || linhaPlanoInicial}
          consultorId={consultorId || ''}
          consultorNome={consultorNome}
          currentUserId={userProfile?.id}
          onSolicitarAutomatico={() => {
            // Disparo real fica em SolicitarFaturaButton (chama useSolicitarFatura)
          }}
        />
      )}
    </div>
  );
}
